import React, {
    ChangeEvent,
    JSX,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import "./styles/SpeakerSearch.css";
import { TextField } from "@mui/material";
import { scrollToTop } from "./util";

type Playlist = {
    year: number;
    url: string;
};

type SoundCloudSound = {
    id: number;
    title: string;
};

type SearchTrack = {
    id: string;
    title: string;
    year: number;
    playlistIndex: number;
};

type SoundCloudWidget = {
    bind: (eventName: string, callback: () => void) => void;
    getSounds: (callback: (sounds: SoundCloudSound[]) => void) => void;
    skip: (soundIndex: number) => void;
    play: () => void;
    pause: () => void;
};

type SoundCloudWidgetFactory = {
    (iframe: HTMLIFrameElement): SoundCloudWidget;
    Events: {
        READY: string;
    };
};

declare global {
    interface Window {
        SC?: {
            Widget: SoundCloudWidgetFactory;
        };
    }
}

const playlists: Playlist[] = [
    {
        year: 2024,
        url: "https://w.soundcloud.com/player/?url=https%3A//api.soundcloud.com/playlists/2050630953%3Fsecret_token%3Ds-6vPadWg0AI8&color=%23ff5500&auto_play=false&hide_related=false&show_comments=false&show_user=false&show_reposts=false&show_teaser=true&visual=false",
    },
    {
        year: 2025,
        url: "https://w.soundcloud.com/player/?url=https%3A//api.soundcloud.com/playlists/2050631808%3Fsecret_token%3Ds-jY9dQJ8kPS7&color=%23ff5500&auto_play=false&hide_related=false&show_comments=false&show_user=false&show_reposts=false&show_teaser=true&visual=false",
    },
    {
        year: 2026,
        url: "https://w.soundcloud.com/player/?url=https%3A//api.soundcloud.com/playlists/soundcloud%3Aplaylists%3A2175050717%3Fsecret_token%3Ds-slJcMSlbuYU&color=%23ff5500&auto_play=false&hide_related=false&show_comments=false&show_user=false&show_reposts=false&show_teaser=true&visual=false",
    },
];

export default function SpeakerSearch(): JSX.Element {
    const iframeRefs = useRef<Record<number, HTMLIFrameElement | null>>({});

    const widgetRefs = useRef<Record<number, SoundCloudWidget | undefined>>({});

    const [tracks, setTracks] = useState<SearchTrack[]>([]);
    const [query, setQuery] = useState("");
    const [selectedTrack, setSelectedTrack] = useState<SearchTrack | null>(
        null
    );
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState("");

    useEffect(() => {
        let isCancelled = false;
        const loadedYears = new Set<number>();

        const initializeWidgets = (): void => {
            const widgetFactory = window.SC?.Widget;

            if (!widgetFactory) {
                setLoadError("The SoundCloud player could not be loaded.");
                setIsLoading(false);
                return;
            }

            playlists.forEach(({ year }) => {
                const iframe = iframeRefs.current[year];

                if (!iframe) {
                    return;
                }

                const widget = widgetFactory(iframe);
                widgetRefs.current[year] = widget;

                widget.bind(widgetFactory.Events.READY, () => {
                    let previousTrackIds = "";
                    let stableChecks = 0;
                    let attempts = 0;

                    const loadSounds = (): void => {
                        widget.getSounds((sounds) => {
                            if (isCancelled) {
                                return;
                            }

                            attempts += 1;

                            const currentTrackIds = sounds
                                .map((sound) => String(sound.id))
                                .join(",");

                            const tracksWithTitles = sounds.filter((sound) =>
                                Boolean(sound.title?.trim())
                            ).length;

                            const allTracksHaveTitles =
                                sounds.length > 0 &&
                                tracksWithTitles === sounds.length;

                            if (
                                currentTrackIds === previousTrackIds &&
                                allTracksHaveTitles
                            ) {
                                stableChecks += 1;
                            } else {
                                previousTrackIds = currentTrackIds;
                                stableChecks = 0;
                            }

                            const playlistTracks: SearchTrack[] = sounds
                                .map((sound, index) => {
                                    const title = sound.title?.trim();

                                    if (!title) {
                                        return null;
                                    }

                                    return {
                                        id: `${year}-${String(sound.id)}`,
                                        title,
                                        year,
                                        playlistIndex: index,
                                    };
                                })
                                .filter(
                                    (track): track is SearchTrack =>
                                        track !== null
                                );

                            setTracks((currentTracks) => {
                                const otherPlaylistTracks =
                                    currentTracks.filter(
                                        (track) => track.year !== year
                                    );

                                return [
                                    ...otherPlaylistTracks,
                                    ...playlistTracks,
                                ].sort((first, second) => {
                                    if (first.year !== second.year) {
                                        return second.year - first.year;
                                    }

                                    return first.title.localeCompare(
                                        second.title
                                    );
                                });
                            });

                            const isStable = stableChecks >= 2;
                            const reachedAttemptLimit = attempts >= 15;

                            if (
                                (!allTracksHaveTitles || !isStable) &&
                                !reachedAttemptLimit
                            ) {
                                window.setTimeout(loadSounds, 400);
                                return;
                            }

                            loadedYears.add(year);

                            if (loadedYears.size === playlists.length) {
                                setIsLoading(false);
                            }
                        });
                    };

                    loadSounds();
                });
            });
        };

        if (window.SC?.Widget) {
            initializeWidgets();

            return () => {
                isCancelled = true;
            };
        }

        const scriptUrl = "https://w.soundcloud.com/player/api.js";

        const existingScript = document.querySelector<HTMLScriptElement>(
            `script[src="${scriptUrl}"]`
        );

        const handleScriptLoad = (): void => {
            initializeWidgets();
        };

        const handleScriptError = (): void => {
            if (!isCancelled) {
                setLoadError("The SoundCloud player could not be loaded.");
                setIsLoading(false);
            }
        };

        if (existingScript) {
            existingScript.addEventListener("load", handleScriptLoad);

            existingScript.addEventListener("error", handleScriptError);

            return () => {
                isCancelled = true;

                existingScript.removeEventListener("load", handleScriptLoad);

                existingScript.removeEventListener("error", handleScriptError);
            };
        }

        const script = document.createElement("script");

        script.src = scriptUrl;
        script.async = true;
        script.addEventListener("load", handleScriptLoad);
        script.addEventListener("error", handleScriptError);

        document.body.appendChild(script);

        return () => {
            isCancelled = true;
            script.removeEventListener("load", handleScriptLoad);
            script.removeEventListener("error", handleScriptError);
        };
    }, []);

    const results = useMemo<SearchTrack[]>(() => {
        const searchValue = query.trim().toLowerCase();

        if (!searchValue) {
            return [];
        }

        return tracks.filter((track) =>
            `${track.title} ${track.year}`.toLowerCase().includes(searchValue)
        );
    }, [query, tracks]);

    const handleSearchChange = (event: ChangeEvent<HTMLInputElement>): void => {
        setQuery(event.target.value);
        if (selectedTrack) {
            widgetRefs.current[selectedTrack.year]?.pause();
        }
        setSelectedTrack(null);
    };

    const handleTrackSelect = (track: SearchTrack): void => {
        setSelectedTrack(track);
        scrollToTop();

        const widget = widgetRefs.current[track.year];

        if (!widget) {
            return;
        }

        widget.skip(track.playlistIndex);
        widget.play();
    };

    const handleBackToResults = (): void => {
        if (selectedTrack) {
            widgetRefs.current[selectedTrack.year]?.pause();
        }
        setSelectedTrack(null);
    };

    return (
        <main className="recording-search">
            <TextField
                id="recording-search-input"
                type="search"
                value={query}
                label="Search Speakers"
                placeholder="Enter a speaker's name or date to begin searching"
                onChange={handleSearchChange}
                disabled={isLoading}
                sx={{ display: "flex" }}
            />

            {isLoading && (
                <p>
                    Loading recordings, please wait to finish before searching…
                </p>
            )}

            {loadError && <p role="alert">{loadError}</p>}

            {!isLoading &&
                !loadError &&
                query.trim() &&
                results.length === 0 && <p>No matching recordings found.</p>}

            {!selectedTrack && results.length > 0 && (
                <ul className="recording-results">
                    {results.map((track) => (
                        <li key={track.id}>
                            <button
                                type="button"
                                onClick={() => handleTrackSelect(track)}
                            >
                                {track.title} ({track.year})
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            {selectedTrack && (
                <section aria-labelledby="selected-track-title">
                    <h2 id="selected-track-title">{selectedTrack.title}</h2>

                    <button type="button" onClick={handleBackToResults}>
                        Back to results
                    </button>
                </section>
            )}

            <div className="soundcloud-playlists">
                {playlists.map(({ year, url }) => {
                    const isSelected = selectedTrack?.year === year;

                    return (
                        <iframe
                            key={year}
                            ref={(element) => {
                                iframeRefs.current[year] = element;
                            }}
                            title={`${year} SoundCloud playlist`}
                            width="100%"
                            height="450"
                            scrolling="no"
                            frameBorder="0"
                            allow="autoplay"
                            src={url}
                            tabIndex={isSelected ? 0 : -1}
                            aria-hidden={!isSelected}
                            className={
                                isSelected
                                    ? "soundcloud-playlist soundcloud-playlist--visible"
                                    : "soundcloud-playlist soundcloud-playlist--hidden"
                            }
                        />
                    );
                })}
            </div>
        </main>
    );
}
